import type { AttributeDistributionType } from 'types/api/diagnosisReportRefResponseType';

export type AttributeKey = 'gender' | 'ageGroup' | 'prefecture';

export const GENDER_ORDER: readonly string[] = ['女性', '男性', 'その他', '回答しない'];

export const AGE_GROUP_ORDER: readonly string[] = [
  '10代以下',
  '20代',
  '30代',
  '40代',
  '50代',
  '60代',
  '70代以上',
];

/**
 * 選択された属性のデータを取得する。
 * 移行元 getCurrentData() と完全一致の挙動を維持する。
 */
export function getAttributeData(
  attributeDistribution: AttributeDistributionType,
  selected: AttributeKey
): { label: string; count: number }[] {
  switch (selected) {
    case 'gender': {
      const data = [...attributeDistribution.gender];
      return data.sort((a, b) => {
        const indexA = GENDER_ORDER.indexOf(a.label);
        const indexB = GENDER_ORDER.indexOf(b.label);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }
    case 'ageGroup': {
      const countByLabel = new Map(attributeDistribution.ageGroup.map(d => [d.label, d.count]));
      return AGE_GROUP_ORDER.map(label => ({
        label,
        count: countByLabel.get(label) ?? 0,
      }));
    }
    case 'prefecture':
      return attributeDistribution.prefecture;
    default:
      return [];
  }
}

/**
 * 属性ごとのバー色を返す。
 * 移行元 getBarColor() と完全一致の挙動を維持する。
 */
export function getBarColor(label: string, attributeType: string): string {
  if (attributeType === 'gender') {
    if (label === '女性') return '#E91E63'; // ピンク
    if (label === '男性') return '#4A90E2'; // 青
    if (label === 'その他') return '#FFC107'; // オレンジ
    if (label === '回答しない') return '#28a745'; // 緑
  } else if (attributeType === 'ageGroup') {
    if (label === '10代以下') return '#A6A6A6'; // グレー
    if (label === '20代') return '#4A90E2'; // 青
    if (label === '30代') return '#28a745'; // 緑
    if (label === '40代') return '#FFC107'; // オレンジ
    if (label === '50代') return '#FF9800'; // オレンジ2
    if (label === '60代') return '#9C27B0'; // 紫
    if (label === '70代以上') return '#795548'; // ブラウン
  }
  return '#4350b4';
}

/**
 * 表示用の派生データを算出する。
 * 移行元の派生計算と完全一致の挙動を維持する。
 *
 * isGenderNoDataOnly: gender かつ 1 件かつ「回答しない」のみの場合、空表示にする。
 * （移行元は currentData[0].label だが、length===1 前提のため等価。
 *   noUncheckedIndexedAccess 対応で optional chaining を付与=挙動不変・安全側）
 */
export function computeDisplay(
  currentData: { label: string; count: number }[],
  selected: AttributeKey
): {
  displayData: { label: string; count: number }[];
  displayTotalCount: number;
  maxCount: number;
} {
  const totalCount = currentData.reduce((sum, item) => sum + item.count, 0);

  const isGenderNoDataOnly =
    selected === 'gender' && currentData.length === 1 && currentData[0]?.label === '回答しない';

  const displayData = isGenderNoDataOnly ? [] : currentData;
  const displayTotalCount = isGenderNoDataOnly ? 0 : totalCount;
  const maxCount = displayData.length > 0 ? Math.max(...displayData.map(d => d.count)) : 0;

  return { displayData, displayTotalCount, maxCount };
}
